import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ArrowRight, Loader2, Heart } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PreorderSuccessPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [customerName, setCustomerName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollStatus(sessionId, 0);
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    if (attempt >= 6) {
      setStatus("timeout");
      return;
    }
    try {
      const res = await fetch(`${BACKEND}/api/preorder/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        setStatus("paid");
        setCustomerName(data.customer_name || "");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      // Keep polling
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2500);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(30_25%_98%)] flex items-center justify-center px-6" data-testid="preorder-success-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-1 mb-8">
          <span className="font-serif text-xl font-bold text-foreground">Io</span>
          <span className="font-serif text-xl font-bold text-accent">Ci</span>
          <span className="font-serif text-xl font-bold text-foreground">Sono</span>
          <span className="text-[10px] text-muted-foreground font-sans ml-0.5">.org</span>
        </div>

        {status === "checking" && (
          <Card className="bg-card border-border/30 shadow-elevated">
            <CardContent className="p-10 text-center">
              <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto mb-4" />
              <h2 className="font-serif text-xl font-bold text-foreground mb-2">Verifica pagamento...</h2>
              <p className="text-sm text-muted-foreground">Stiamo confermando il tuo pre-ordine</p>
            </CardContent>
          </Card>
        )}

        {status === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <Card className="bg-card border-border/30 shadow-elevated">
              <CardContent className="p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="font-serif text-2xl font-bold text-foreground mb-2">
                  Grazie{customerName ? `, ${customerName}` : ""}!
                </h2>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  Il tuo pre-ordine Founder Access e confermato.<br />
                  Ti contatteremo presto con le istruzioni per accedere alla piattaforma.
                </p>
                <div className="space-y-2 mb-6 text-left max-w-xs mx-auto">
                  {[
                    "Accesso anticipato alla piattaforma",
                    "1 profilo completo con AI",
                    "60 min di registrazione voce",
                    "Sconto lifetime 50%",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
                <Button variant="premium" onClick={() => nav("/register")} data-testid="success-register-btn">
                  Crea il tuo account <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <Card className="bg-card border-border/30 shadow-elevated">
            <CardContent className="p-10 text-center">
              <Heart className="w-8 h-8 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="font-serif text-xl font-bold text-foreground mb-2">
                {status === "timeout" ? "Verifica in corso" : "Sessione non valida"}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {status === "timeout"
                  ? "Il pagamento potrebbe essere ancora in elaborazione. Controlla la tua email."
                  : "La sessione di pagamento non e valida o e scaduta."}
              </p>
              <Button variant="outline-primary" onClick={() => nav("/preordine")} data-testid="back-preorder-btn">
                Torna al pre-ordine
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
